import { Fragment, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Plan, StripePrice } from '../types'
import PlanCard from './PlanCard'
import SubscriptionForm from './SubscriptionForm'
import SubscriptionTerms from './SubscriptionTerms'

type SubscriptionPlan = 'top' | 'first' | 'second'

const DEFAULT_SUBSCRIPTION_PLAN_ORDER: SubscriptionPlan[] = [
  'top',
  'first',
  'second',
]

interface Props {
  buttonTitle?: string
  countdownUntil: number
  currency: string
  firstIntroPrice: StripePrice | undefined
  firstPerk?: string
  firstPrice: StripePrice | undefined
  isSubmitting: boolean
  oneTimePrice?: StripePrice | undefined
  onPlanChange: (plan: Plan) => void
  secondIntroPrice: StripePrice | undefined
  secondPerk?: string
  secondPrice: StripePrice | undefined
  selectedPlan: Plan
  setIsSubmitting: (isSubmitting: boolean) => void
  showMoneyBackGuarantee?: boolean
  subscriptionPlanOrder?: SubscriptionPlan[]
  topIntroPrice?: StripePrice | undefined
  topPerk?: string
  topPrice?: StripePrice | undefined
  trialPeriodDays?: number
}

export default function Plans({
  buttonTitle,
  countdownUntil,
  currency,
  firstIntroPrice,
  firstPerk,
  firstPrice,
  isSubmitting,
  oneTimePrice,
  onPlanChange,
  secondIntroPrice,
  secondPerk,
  secondPrice,
  selectedPlan,
  setIsSubmitting,
  showMoneyBackGuarantee = true,
  subscriptionPlanOrder = DEFAULT_SUBSCRIPTION_PLAN_ORDER,
  topIntroPrice,
  topPerk,
  topPrice,
  trialPeriodDays,
}: Props) {
  const { t } = useTranslation()

  const getPriceId = () => {
    switch (selectedPlan) {
      case 'top':
        return topIntroPrice?.id ?? topPrice?.id
      case 'first':
        return firstIntroPrice?.id ?? firstPrice?.id
      case 'second':
        return secondIntroPrice?.id ?? secondPrice?.id
      case 'one-time':
        return oneTimePrice?.id
    }
  }

  const getStripeFullPrice = () => {
    switch (selectedPlan) {
      case 'top':
        return topPrice
      case 'first':
        return firstPrice
      case 'second':
        return secondPrice
      case 'one-time':
        return oneTimePrice
    }
  }

  const getStripeIntroPrice = () => {
    switch (selectedPlan) {
      case 'top':
        return topIntroPrice
      case 'first':
        return firstIntroPrice
      case 'second':
        return secondIntroPrice
      case 'one-time':
        return undefined
    }
  }

  const priceId = getPriceId()
  const stripeFullPrice = getStripeFullPrice()
  const stripeIntroPrice = getStripeIntroPrice()
  const isOneTimeSelected = selectedPlan === 'one-time'

  const subscriptionPlanCards: Record<SubscriptionPlan, ReactNode> = {
    top: topPrice ? (
      <PlanCard
        bestOffer={true}
        countdownUntil={countdownUntil}
        currency={currency}
        isSelected={selectedPlan === 'top'}
        isSubmitting={isSubmitting}
        onSelect={() => onPlanChange('top')}
        perk={topPerk}
        plan="top"
        setIsSubmitting={setIsSubmitting}
        stripePriceFull={topPrice}
        stripePriceIntro={topIntroPrice}
        trialPeriodDays={trialPeriodDays}
      />
    ) : null,
    first: firstPrice ? (
      <PlanCard
        bestOffer={!topPrice}
        countdownUntil={countdownUntil}
        currency={currency}
        isSelected={selectedPlan === 'first'}
        isSubmitting={isSubmitting}
        onSelect={() => onPlanChange('first')}
        perk={firstPerk}
        plan="first"
        setIsSubmitting={setIsSubmitting}
        stripePriceFull={firstPrice}
        stripePriceIntro={firstIntroPrice}
        trialPeriodDays={trialPeriodDays}
      />
    ) : null,
    second: secondPrice ? (
      <PlanCard
        countdownUntil={countdownUntil}
        currency={currency}
        isSelected={selectedPlan === 'second'}
        isSubmitting={isSubmitting}
        onSelect={() => onPlanChange('second')}
        perk={secondPerk}
        plan="second"
        setIsSubmitting={setIsSubmitting}
        stripePriceFull={secondPrice}
        stripePriceIntro={secondIntroPrice}
        trialPeriodDays={trialPeriodDays}
      />
    ) : null,
  }

  const oneTimeCard = oneTimePrice ? (
    <PlanCard
      countdownUntil={countdownUntil}
      currency={currency}
      isSelected={selectedPlan === 'one-time'}
      isSubmitting={isSubmitting}
      onSelect={() => onPlanChange('one-time')}
      plan="one-time"
      setIsSubmitting={setIsSubmitting}
      stripePriceFull={oneTimePrice}
      trialPeriodDays={trialPeriodDays}
    />
  ) : null

  return (
    <div className="space-y-4 sm:space-y-6">
      {subscriptionPlanOrder.map((planKey) => (
        <Fragment key={planKey}>
          {subscriptionPlanCards[planKey]}
          {/* Preserve the historical position of the one-time card (rendered
              right after the "first" plan). */}
          {planKey === 'first' && oneTimeCard}
        </Fragment>
      ))}

      <div className="space-y-3">
        <SubscriptionForm
          buttonClassName="custom-button-primary uppercase"
          buttonTitle={buttonTitle ?? t('pages.checkout.plans.continue')}
          currency={currency}
          isSubmitting={isSubmitting}
          plan={selectedPlan}
          priceId={priceId}
          setIsSubmitting={setIsSubmitting}
          stripePriceFull={stripeFullPrice}
          trialPeriodDays={trialPeriodDays}
        />

        {isOneTimeSelected ? (
          showMoneyBackGuarantee && (
            <p className="text-center font-bold">
              {t('pages.checkout.plans.moneyBackGuarantee')}
            </p>
          )
        ) : (
          <>
            <p className="text-center font-bold">
              {t('pages.checkout.plans.cancelAnytime')}
            </p>

            {showMoneyBackGuarantee && (
              <p className="text-center font-bold">
                {t('pages.checkout.plans.moneyBackGuarantee')}
              </p>
            )}
          </>
        )}
      </div>

      <SubscriptionTerms
        currency={currency}
        plan={selectedPlan}
        stripePriceFull={stripeFullPrice}
        stripePriceIntro={stripeIntroPrice}
        trialPeriodDays={trialPeriodDays}
      />
    </div>
  )
}
